<template>
  <header :style="{ backgroundColor: theme.accentColor }">
    <!-- Mobile Active Subcategory -->
    <div v-if="isInMobileSubcategoryView" class="header-row">
      <div class="left-col">
        <i
          class="icon-Arrow-Left"
          @click="goBackToCategories"
          role="button"
          aria-label="Go Back"
        ></i>
        <p class="subcategory-title">{{ activeSubcategoryTitle }}</p>
      </div>

      <div class="right-col">
        <i class="icon-Share-3" @click="handleShareSubcategoryClick"></i>
        <i
          role="button"
          aria-label="Collapse All"
          class="icon-Collapse-All"
          @click="toggleExpandAll(false)"
        ></i>

        <i
          role="button"
          aria-label="Expand All"
          class="icon-Expand-All"
          @click="toggleExpandAll(true)"
        ></i>
      </div>
    </div>

    <!-- Always visible for desktop -->
    <div v-else class="header-row">
      <!-- Avatar & Contacts -->
      <div class="left-col">
        <img v-if="propertyInfo.avatar" :src="avatarSrc" alt="avatar" :class="avatarClasses" />
        <i
          role="button"
          aria-label="email link"
          v-if="emailContactLink"
          class="icon-Send"
          @click="contactHost(ContactTypes.EMAIL)"
          :style="{ marginLeft: !!propertyInfo.avatar ? ICON_MARGIN : '0' }"
        ></i>

        <i
          role="button"
          aria-label="phone link"
          v-if="phoneContactLink"
          class="icon-Phone"
          @click="contactHost(ContactTypes.PHONE)"
        ></i>

        <i
          role="button"
          aria-label="whatsapp link"
          v-if="whatsappLink"
          class="icon-WhatsApp"
          @click="openWhatsAppLink"
        >
        </i>
      </div>

      <div class="right-col">
        <i role="button" aria-label="Share" class="icon-Share-3" @click="handleShareGuideClick"></i>

        <!-- Print PDF -->
        <i
          role="button"
          aria-label="Fetch PDF"
          class="icon-Printer"
          @click="fetchPdf"
          v-if="!data.isFetchingPdf"
        ></i>

        <!-- Loading PDF Indicator -->
        <i class="icon-Spinner" v-show="data.isFetchingPdf"></i>
      </div>
    </div>
  </header>
</template>

<script lang="ts" setup>
import { generateFileFromResponse, isMobile, sendPlausibleEvent } from '@/utils/helpers'
import { Contact } from '@/interfaces/state.interface'
import api from '@/api'
import { LogoStylesEnum } from '@/interfaces/api.interface'
import { ContactTypes, PlausibleEvents } from '@/utils/consts'
import { computed, reactive } from 'vue'
import { Category, ShareTypesEnum } from '@/interfaces/guide.interface'
import { useRoute, useRouter } from 'vue-router'
import { ElNotification } from 'element-plus'
import { useHelperStore } from '@/pinia/helper.store'
import { useMainStore } from '@/pinia/main.store'

const helperStore = useHelperStore()
const mainStore = useMainStore()
const router = useRouter()
const route = useRoute()

const props = defineProps<{ activeCategory: Category | null }>()
const emit = defineEmits<{
  (e: 'expandAll', expandAll: boolean): void
  (e: 'show-share-overlay', { type: ShareTypesEnum }): void
}>()

const data = reactive({
  isFetchingPdf: false
})

const ICON_MARGIN = '10px'
const theme = computed(() => helperStore.styleData)
const guideToken = computed(() => helperStore.guideToken)
const currentLanguage = computed(() => helperStore.currentLanguage)
const propertyInfo = computed(() => mainStore.propertyInfo)
const activeSubcategoryTitle = computed(
  () => mainStore.activeSubcategory?.title_translations?.[helperStore.currentLanguage]
)
const avatarClasses = computed(() => [
  'avatar',
  { 'avatar--circle': propertyInfo.value.avatar.logo_style === LogoStylesEnum.CIRCLE }
])
const avatarSrc = computed(() => propertyInfo.value.avatar?.url || propertyInfo.value.avatar)
const phoneContactLink = computed(() => {
  if (!propertyInfo?.value) {
    return ''
  }

  const contact = propertyInfo.value.contacts?.find(
    (contact: Contact) => contact.type === ContactTypes.PHONE && contact.value
  )

  return contact ? `${propertyInfo.value.contact_type}:${contact.value}` : ''
})
const whatsappLink = computed(() => {
  const contact = propertyInfo.value?.contacts.find((c) => c.type === ContactTypes.WHATS_APP)

  return contact.value
})

const emailContactLink = computed(() => {
  if (!propertyInfo?.value) {
    return ''
  }

  const contact = propertyInfo.value.contacts?.find(
    (contact: Contact) => contact.type === ContactTypes.EMAIL
  )

  return contact?.value ? `mailto:${contact.value}` : ''
})
const isInMobileSubcategoryView = computed(() => isMobile() && route.params.subcategoryId)

function goBackToCategories() {
  const hasMoreThanOneSubcategory = props.activeCategory?.subcategories.length > 1

  if (hasMoreThanOneSubcategory) {
    router.push({
      name: route.name,
      params: { subcategoryId: null },
      query: { categoryId: props.activeCategory.id }
    })

    return
  }

  router.push({ name: route.name, params: { subcategoryId: null } })
}

function toggleExpandAll(expandAll: boolean) {
  emit('expandAll', expandAll)
}

function openWhatsAppLink() {
  sendPlausibleEvent(PlausibleEvents.WHATSAPP_HOST_CLICK)
  window.open(whatsappLink.value, '_blank')
}

async function fetchPdf() {
  ElNotification({
    type: 'info',
    title: 'Info',
    message: helperStore.i18n('we_are_just_preparing_your_pdf'),
    duration: 0
  })

  sendPlausibleEvent(PlausibleEvents.PRINT_PDF_CLICK)

  data.isFetchingPdf = true

  const INTERVAL_TIME = 10000
  const PDF_TIMEOUT = 30 * INTERVAL_TIME
  let interval
  let totalTimePassed = -INTERVAL_TIME // to negate the initial addition
  let res = await api.initialisePdfGeneration(guideToken.value, currentLanguage.value)

  if (res.error) {
    ElNotification({
      title: 'Error',
      message: helperStore.i18n('pdf_error_message'),
      type: 'error',
      duration: 0
    })

    data.isFetchingPdf = false
    return
  }

  if (res.in_progress) {
    interval = setInterval(async () => {
      totalTimePassed += INTERVAL_TIME // when 30 (300 seconds) -> throw an error

      res = await api.getPdfStatus(guideToken.value, currentLanguage.value, res.task_id)

      if (res.error || totalTimePassed >= PDF_TIMEOUT) {
        ElNotification({
          title: 'Error',
          message: helperStore.i18n('pdf_error_message'),
          type: 'error',
          duration: 0
        })
        data.isFetchingPdf = false
        clearInterval(interval)
        return
      }

      if (!res.in_progress) {
        generateFileFromResponse(res, propertyInfo.value.name)
        data.isFetchingPdf = false
        clearInterval(interval)
      }
    }, INTERVAL_TIME)
  } else {
    generateFileFromResponse(res, propertyInfo.value.name)
    data.isFetchingPdf = false
  }
}
function contactHost(type: ContactTypes) {
  const a = document.createElement('a')

  switch (type) {
    case ContactTypes.EMAIL:
      sendPlausibleEvent(PlausibleEvents.EMAIL_HOST_CLICK)
      break
    case ContactTypes.PHONE:
      sendPlausibleEvent(PlausibleEvents.CALL_HOST_CLICK)
      break
  }

  a.href = type === ContactTypes.EMAIL ? emailContactLink.value : phoneContactLink.value
  a.dispatchEvent(new MouseEvent('click'))
}

function handleShareGuideClick() {
  sendPlausibleEvent(PlausibleEvents.SHARE_GUIDE_CLICK)
  emit('show-share-overlay', { type: ShareTypesEnum.GUIDE })
}

function handleShareSubcategoryClick() {
  emit('show-share-overlay', { type: ShareTypesEnum.SUBCATEGORY })
}
</script>

<style lang="scss" scoped>
header {
  width: 100%;
  background-color: $primary-accent;
  color: white;
  height: $header-height;
  display: flex;
  align-items: center;

  @include mediaDesktop {
    border-radius: $border-radius $border-radius 0 0;
  }
}

.icon-Spinner {
  animation: rotation 2s infinite linear;
  padding-left: 0 !important;
  margin-left: 10px;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 15px;

  :deep(.right-col .el-dropdown) {
    display: block;
  }

  .right-col,
  .left-col {
    display: flex;
    align-items: center;

    i {
      font-size: 1.5rem;
      margin-left: 10px;
      text-shadow: $text-shadow--icon;

      &.icon-Arrow-Left {
        margin-left: 0;
      }
    }

    p {
      text-shadow: $text-shadow--black;
    }
  }

  .left-col {
    max-width: 70%;
  }

  .right-col {
    flex: 0 1 auto;
  }

  .subcategory-title {
    padding-left: 5px;
    text-overflow: ellipsis;
    font-size: 1.25rem;
    font-weight: bold;
    overflow: hidden;
    white-space: nowrap;
  }

  .avatar {
    $avatar-dimension: 45px;

    max-width: 50%;
    min-width: $avatar-dimension;
    height: $avatar-dimension;
    object-fit: contain;

    &--circle {
      border-radius: 50%;
      border: 2px solid currentColor;
    }
  }
}
</style>
